import { TimeIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import Card from "components/card/Card";
import { IconArrowRight, IconTranslator } from "components/icons/Icons";
import moment from "moment-timezone";
import { fetchUnreadMessagesCnt, pad_with_zeroes } from "network/helper";
import { Fragment} from "react";
import { useSelector } from "react-redux";
import { unreadMessageSelector } from "store/project/selector";
import { useTransform } from "views/admin/projects/components/projectsTable/helpers/dataTransform";

type RowObj = {
  id: string;
  order_id: string;
  order_name: string;
  order_due_at: string;
  order_status_name: string;
  translation_type: string;
  languages: string;
  translated_from: string;
  translated_to: string;
  notificationType: 0 | 1 | 2 | 3 | 4 | 5;
  message_cnt: number;
  actionCol: string;
  include_expedited_turnaround: string;
  order_status_id: string;
  document_status_id: number;
};

const columnHelper = createColumnHelper<RowObj>();

export default function TranslatorProjectsTable(props: {
  tableData: any;
  isFetching?: boolean;
  onShowTranslatorProjectDetails: Function;
}) {
  const { tableData, isFetching, onShowTranslatorProjectDetails } = props;
  const textColor = useColorModeValue("black", "black");
  const unreadMessages = useSelector(unreadMessageSelector);

  let defaultData = useTransform(tableData);
  const mobileColumnWidths: any = {
    id: 100,
    order_due_at: 120,
    status: 230,
    languages: 160,
    notificationType: 190,
    actionCol: 80,
  };
  const columns = [
    columnHelper.accessor("id", {
      id: "id",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Project #
        </Text>
      ),
      cell: (info: any) => {
        return (
          <Flex align="center">
            <Text
              color={info.row.getValue("id") === "0012" ? "#F00" : textColor}
              fontSize="14px"
              lineHeight="24px"
              fontWeight="400"
              maxW={{ sm: "200px", md: "unset" }}
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              {pad_with_zeroes(info.getValue(), 6)}
            </Text>
          </Flex>
        );
      },
    }),

    columnHelper.accessor("order_due_at", {
      id: "order_due_at",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Due Date
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text
            color={textColor}
            fontSize="14px"
            lineHeight="24px"
            fontWeight="400"
          >
            {info.getValue()
              ? moment(new Date(info.getValue())).format("MM/DD/YY")
              : "-"}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("order_status_name", {
      id: "order_status_name",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Status
        </Text>
      ),
      cell: (info) => {
        const documentStatusId = info.row.original?.document_status_id;
        const orderStatusName = documentStatusId >= 3 ? "Completed" : 
          documentStatusId ==2 ? "In Progress" : "New";
        return (
          <Text
            color={textColor}
            fontSize="14px"
            lineHeight="24px"
            fontWeight="400"
            minWidth="50px"
            width="fit-content"
            height={"34px"}
            borderRadius="100px"
            padding="5px 20px"
            bgColor="#F3F5F5"
            textTransform="capitalize"
            whiteSpace="nowrap"
          >
            {orderStatusName}
          </Text>
        );
      } 
    }),
    columnHelper.accessor("translation_type", {
      id: "translation_type",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Translation Type
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="14px"
          lineHeight="24px"
          fontWeight="400"
          width="fit-content"
          height={"34px"}
          whiteSpace="nowrap"
          borderRadius="100px"
          padding="5px 15px"
          bgColor="#F3F5F5"
          textTransform="capitalize"
        >
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor("languages", {
      id: "languages",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Languages
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Flex
            color={textColor}
            fontSize="14px"
            lineHeight="24px"
            fontWeight="400"
            alignItems={"center"}
          >
            {info.row.original?.translated_from &&
              info.row.original?.translated_to && (
                <Fragment>
                  {info.row.original?.translated_from}
                  <IconTranslator width="15px" height="16px" mx="4px" />
                  {info.row.original?.translated_to}
                </Fragment>
              )}
          </Flex>
        </Flex>
      ),
    }),
    columnHelper.accessor("notificationType", {
      id: "notificationType",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Notifications
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          {fetchUnreadMessagesCnt(
            unreadMessages,
            Number(info.row.original.id)
          ) > 0 && (
            <Text
              color="#FFF"
              bgColor="#F02828"
              borderRadius="100px"
              fontSize="14px"
              lineHeight="24px"
              fontWeight="400"
              padding="5px 20px"
              whiteSpace="nowrap"
            >
              {`${fetchUnreadMessagesCnt(
                unreadMessages,
                Number(info.row.original.id)
              )} New Message`}
            </Text>
          )}
        </Flex>
      ),
    }),

    columnHelper.accessor("actionCol", {
      id: "actionCol",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          color="gray.500"
          fontWeight="400"
        ></Text>
      ),
      cell: (info) => (
        <Flex align="center" width={"40px"} mr="0">
          <IconButton
            aria-label="search"
            bg="inherit"
            borderRadius="inherit"
            onClick={(e) => {
              onShowTranslatorProjectDetails(info.row.original.order_id);
            }}
            _active={{
              bg: "inherit",
              transform: "none",
              borderColor: "transparent",
            }}
            _hover={{
              bg: "inherit",
            }}
            _focus={{
              boxShadow: "none",
            }}
            icon={<IconArrowRight w="49px" h="50px" />}
          />

          {Number(info.row.original.include_expedited_turnaround) === 1 &&
            Number(info.row.original.order_status_id) !== 6 &&
            Number(info.row.original.order_status_id) !== 10 && (
              <Tooltip label="Expedited Order">
                <TimeIcon
                  w="20px"
                  h="20px"
                  color="#F02828"
                  sx={{
                    position: "absolute",
                    right: "-8px",
                    top: "-8px",
                    borderRadius: "50%",
                    bgColor: "#FFF",
                  }}
                />
              </Tooltip>
            )}
        </Flex>
      ),
    }),
  ];
  const data = [...defaultData];
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });
  return (
    <Card
      flexDirection="column"
      w="calc(100% + 20px)"
      p="0px"
      px="10px"
      ml="-10px"
      overflowX={{ sm: "scroll", lg: "hidden" }}
    >
      <Box>
        <Table
          variant="simple"
          color="gray.500"
          mb="24px"
          pb="2"
          lineHeight="15px"
        >
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      px={{ lg: "20px", md: "10px", base: "10px" }}
                      border={"none"}
                      fontFamily="Inter"
                      cursor="pointer"
                      textTransform="capitalize"
                    >
                      <Flex
                        justifyContent="space-between"
                        align="center"
                        fontSize={{ sm: "10px", lg: "12px" }}
                        lineHeight="24px"
                        color="gray.500"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted() as string] ?? null}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {!isFetching &&
              table.getRowModel().rows.map((row) => {
                return (
                  <Fragment key={row.id}>
                    <Tr
                      mb={"10px"}
                      position="relative"
                      cursor="pointer"
                      zIndex={1}
                      onClick={() => {
                        onShowTranslatorProjectDetails(row.original.order_id);
                      }}
                      _after={{
                        content: '""',
                        position: "absolute",
                        // border: "1px solid #DCDCDC",
                        border:
                          Number(row.original.include_expedited_turnaround) ===
                            1 &&
                          Number(row.original.order_status_id) !== 6 &&
                          Number(row.original.order_status_id) !== 10
                            ? "1px solid #EE000055"
                            : "1px solid #DCDCDC",
                        bgColor:
                          Number(row.original.include_expedited_turnaround) ===
                            1 &&
                          Number(row.original.order_status_id) !== 6 &&
                          Number(row.original.order_status_id) !== 10
                            ? "#EE000022"
                            : "transparent",
                        borderRadius: "10px",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: "-1",
                        transition: "all .25s ease-in-out",
                      }}
                      _hover={{
                        _after: {
                          // backgroundColor: "#F1F1F1",
                          // borderColor: "#F1F1F1",
                          backgroundColor:
                            Number(
                              row.original.include_expedited_turnaround
                            ) === 1 &&
                            Number(row.original.order_status_id) !== 6 &&
                            Number(row.original.order_status_id) !== 10
                              ? "#EE000020"
                              : "#F1F1F1",
                          borderColor:
                            Number(
                              row.original.include_expedited_turnaround
                            ) === 1 &&
                            Number(row.original.order_status_id) !== 6 &&
                            Number(row.original.order_status_id) !== 10
                              ? "#EE000055"
                              : "#F1F1F1",
                          boxShadow: "0px 2px 6px 3px rgba(0, 0, 0, 0.1)",
                        },
                      }}
                      _active={{
                        _after: {
                          boxShadow: "none",
                        },
                      }}
                    >
                      {row.getVisibleCells().map((cell, index: number) => {
                        return (
                          <Td
                            padding={{
                              lg: "20px",
                              md: "20px 10px",
                              base: "20px 10px",
                            }}
                            key={cell.id}
                            fontSize={{ sm: "14px" }}
                            minW={{
                              base: `${mobileColumnWidths[cell.column.id]}px`,
                              md: `${mobileColumnWidths[cell.column.id]}px`,
                            }}
                            border="none"
                            width={
                              cell.column.id === "actionCol" ? "80px" : "auto"
                            }
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                    <Tr>
                      <Td
                        colSpan={7}
                        paddingTop="5px"
                        paddingBottom="10px"
                        border="none !important"
                      ></Td>
                    </Tr>
                  </Fragment>
                );
              })}

            {isFetching &&
              [1, 2, 3, 4, 5].map((row) => {
                return (
                  <Fragment key={`skeleton-row-${row}`}>
                    <Tr
                      zIndex="1"
                      position="relative"
                      _after={{
                        content: '""',
                        position: "absolute",
                        border: "1px solid #DCDCDC",
                        borderRadius: "10px",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: "-1",
                        transition: "all .25s ease-in-out",
                      }}
                    >
                      {columns.map((it: any, colIndex: number) => {
                        return (
                          <Td
                            key={`skeleton-${colIndex}`}
                            padding={{
                              lg: "20px",
                              md: "20px 10px",
                              base: "15px 10px",
                            }}
                            border="none !important"
                          >
                            <Skeleton
                              height="25px"
                              colorScheme="#F3F5F5"
                              borderRadius="5px"
                            ></Skeleton>
                          </Td>
                        );
                      })}
                    </Tr>
                    <Tr>
                      <Td
                        colSpan={7}
                        paddingTop="5px"
                        paddingBottom="10px"
                        border="none !important"
                      ></Td>
                    </Tr>
                  </Fragment>
                );
              })}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
}
